import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string, sobrietyStart: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('Attempting sign in for:', email);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('Sign in error:', error);
      throw error;
    }
    console.log('Sign in successful:', data);
  };

  const signUp = async (email: string, password: string, username: string, sobrietyStart: string) => {
    console.log('Attempting sign up for:', email);
    
    // Sign up the user with metadata
    const { data, error: signUpError } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          username,
          sobriety_start: sobrietyStart
        }
      }
    });
    
    if (signUpError) {
      console.error('Sign up error:', signUpError);
      throw signUpError;
    }

    console.log('Sign up successful:', data);
    
    // The database trigger should automatically create the user profile
    // But let's also manually create it as a backup
    if (data.user) {
      try {
        console.log('Attempting to create user profile for:', data.user.id);
        
        const { data: profileData, error: profileError } = await supabase
          .from('users')
          .insert([
            {
              id: data.user.id,
              email,
              username,
              sobriety_start: sobrietyStart,
              is_premium: false,
            },
          ])
          .select(); // Add select() to see what was inserted
        
        if (profileError) {
          console.error('Profile creation error:', profileError);
          console.error('Error details:', {
            code: profileError.code,
            message: profileError.message,
            details: profileError.details,
            hint: profileError.hint
          });
          // Don't throw here as the trigger might have already created it
        } else {
          console.log('Profile created successfully:', profileData);
        }
      } catch (error) {
        console.error('Manual profile creation failed:', error);
        console.error('Error type:', typeof error);
        console.error('Full error object:', error);
        // Continue anyway as the trigger should handle it
      }
    } else {
      console.error('No user data returned from signup');
    }
    
    // Sign out the user after successful signup to redirect to login
    await supabase.auth.signOut();
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

